import React, { useContext, useCallback, useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { makeStyles, Theme, FormControl, FormLabel, Checkbox, FormControlLabel, Button, FormGroup, FormHelperText, Paper } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { capitalizeFirst, useModeFormStyle, useModePanelStyle, SlideOutComp, ModeSelectInputField, SelectInputOption, ModeTextInputField,
    GenericMessageBox, capitalizeAll } from '@moderepo/mode-app-components';
import produce, { Draft } from 'immer';
import { hideSlideOutPanel, modeUIContext, setIsEditingForm } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { ChartMetricInfo, ExportDataFormat, getLongestMetricNameInPixel } from '.';
import { ReadonlyNonEmptyArray } from '../../utils/utils';



const useStyle = makeStyles((theme: Theme) => {
    return {
        content: {
            [theme.breakpoints.up('md')]: {
                minWidth: 600,
            },
        },

        title: {
            fontWeight: 500,
            fontSize  : 'larger',
        },

        formControl: {
            marginBottom: theme.spacing(2),
            width       : '100%',
        },

        formControlLabel: {
            paddingBottom: theme.spacing(1),
        },

        formGroup: {
            margin        : theme.spacing(2, 0),
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            flexWrap      : 'wrap',
            alignSelf     : 'center',
        },

        metric: {
            whiteSpace: 'normal',
            wordBreak : 'break-word',
        },

        metricLabel: {},

        selectAllButton: {
            width     : 'fit-content',
            marginLeft: theme.spacing(2),
        },

        selectFormatButton: {
            width     : 'fit-content',
            marginLeft: theme.spacing(2),
        },

        fileNameInput: {
            width     : 'fit-content',
            marginLeft: theme.spacing(2),
        },
    };
});


interface InputErrors {
    selectedMetrics?: string | undefined;
    fileName?: string | undefined;
}


// The maximum length of the file name
const MAX_FILE_NAME_LENGTH = 100;



export interface MultiDataSourcesExportDataOptionsSlideOutContainerProps {
    readonly projectId: number;
    readonly supportedDataFormats: ReadonlyNonEmptyArray<ExportDataFormat>;
    readonly numericMetrics: readonly ChartMetricInfo[];
    readonly tagMetrics: readonly ChartMetricInfo[];

    // Handler to handle the event when the user finish selecting the metrics and click "Export". Return true if successful and false otherwise
    readonly onExportSelectedMetrics: (
        selectedDataFormat: ExportDataFormat,
        selectedFileName: string,
        selectedNumericMetrics: readonly ChartMetricInfo[],
        selectedTagMetrics: readonly ChartMetricInfo[],
    )=> void;
}


/**
 * This container is used for displaying the options for exporting data for some numeric/tag metrics. This component shows a list of metrics
 * the user select/unselect, the data format to export in, and a file name for the export file.
 */
export const MultiDataSourcesExportDataOptionsSlideOutContainer = (props: MultiDataSourcesExportDataOptionsSlideOutContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { projectId } = props;
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);

    // A map of selected metrics which the key is the metric ID and the value is true/false to indicate if the metric is selected
    // We will initialize with everything is TRUE/SELECTED
    const [selectedMetricsMap, setSelectedMetricsMap] = useState<{
        [metricId: string]: boolean
    }>({
        ...props.numericMetrics.reduce((result, metric) => {
            return {
                ...result,
                [metric.id]: true,
            };
        }, {}),
        ...props.tagMetrics.reduce((result, metric) => {
            return {
                ...result,
                [metric.id]: true,
            };
        }, {}),
    });
    const [supportedDataFormats, setSelectedDataFormat] = useState<ExportDataFormat>(props.supportedDataFormats[0]);
    const [selectedFileName, setSelectedFileName] = useState<string>('data');


    const [inputErrors, setInputErrors] = useState<InputErrors>({});
    const classes = useStyle();
    const formClasses = useModeFormStyle();
    const panelClasses = useModePanelStyle();


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));
    }, [uiDispatch]);


    /**
     * Automatically set is in editing mode to true since this component is used for selecting metrics to be exported therefore it will always
     * be in editing mode.
     */
    useEffect(() => {
        onChangeEditingMode(true);
    }, [onChangeEditingMode]);


    /**
     * Unselected the current selected Alert entity
     */
    const hidePreviewPanel = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
        onChangeEditingMode(false);
    }, [onChangeEditingMode, uiDispatch]);


    /**
     * Toggle one of the data's selection. Also, check if any of the data is selected. If not, show error.
     */
    const onToggleDataSelection = useCallback((metricId: string, value: boolean) => {
        const newSelectedMetricMap = {
            ...selectedMetricsMap,
            [metricId]: value,
        };
        setSelectedMetricsMap(newSelectedMetricMap);

        // Check if there is at least 1 metric selected
        const hasSelectedMetric = Object.values(newSelectedMetricMap).some((selected: boolean) => {
            return selected;
        });

        if (hasSelectedMetric) {
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.selectedMetrics = undefined;
            }));
        } else {
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.selectedMetrics = capitalizeFirst(trans('panel.multi_data_sources.export.no_selection.error'));
            }));
        }
    }, [inputErrors, selectedMetricsMap, trans]);



    /**
     * On selectAll clicked. We will select all data or unselect all data based on 'value'
     */
    const toggleSelectAll = useCallback((value: boolean) => {
        const newSelectedMetricsMap = {
            ...props.numericMetrics.reduce((result, metric) => {
                return {
                    ...result,
                    [metric.id]: value,
                };
            }, {}),
            ...props.tagMetrics.reduce((result, metric) => {
                return {
                    ...result,
                    [metric.id]: value,
                };
            }, {}),
        };


        setSelectedMetricsMap(newSelectedMetricsMap);

        // Update inputErrors.data accordingly based on whether we selectedAll or unselectAll
        if (value) {
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.selectedMetrics = undefined;
            }));
        } else {
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.selectedMetrics = capitalizeFirst(trans('panel.multi_data_sources.export.no_selection.error'));
            }));
        }
    }, [inputErrors, props.numericMetrics, props.tagMetrics, trans]);



    // Check if all data are selected so that we can show the "Select All" and "Unselect All" options accordingly
    const areAllSelected = useMemo(() => {
        return Object.values(props.numericMetrics).every((metric) => {
            return selectedMetricsMap[metric.id];
        }) && Object.values(props.tagMetrics).every((metric) => {
            return selectedMetricsMap[metric.id];
        });
    }, [props.numericMetrics, props.tagMetrics, selectedMetricsMap]);


    const onFormatChanged = useCallback((format: ExportDataFormat) => {
        setSelectedDataFormat(format);
    }, []);


    /**
     * On fileName input changed, update the local state and check for errors.
     */
    const onFileNameChanged = useCallback((newFileName: string) => {
        setSelectedFileName(newFileName);

        if (!newFileName || newFileName.trim().length === 0) {
            // Also show input is required error.
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.fileName = capitalizeFirst(trans('error.input.generic.too_short', { min: 1 }));
            }));
        } else if (newFileName && newFileName.trim().length > MAX_FILE_NAME_LENGTH) {
            // Also show input is required error.
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.fileName = capitalizeFirst(trans('error.input.generic.too_long', { max: MAX_FILE_NAME_LENGTH }));
            }));
        } else if (newFileName) {
            // If newFileName is not empty then clear the previous error message if there is one.
            setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                draft.fileName = undefined;
            }));
        }
    }, [inputErrors, trans]);


    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();


        // filter out data that are not selected
        const selectedNumericMetrics = props.numericMetrics.filter((metric) => {
            return selectedMetricsMap[metric.id];
        });

        const selectedTagMetrics = props.tagMetrics.filter((metric) => {
            return selectedMetricsMap[metric.id];
        });

        if (selectedNumericMetrics.length + selectedTagMetrics.length > 0 && selectedFileName.trim().length > 0 && supportedDataFormats) {
            props.onExportSelectedMetrics(supportedDataFormats, selectedFileName.trim(), selectedNumericMetrics, selectedTagMetrics);
            hidePreviewPanel();
        }

    }, [hidePreviewPanel, props, selectedFileName, supportedDataFormats, selectedMetricsMap]);



    // Find the longest metric name. We will use this value to set the width of the metric label
    const [metricLabelWidthInPixel, setMetricLabelWidthInPixel] = useState<number>(0);

    useLayoutEffect(() => {
        // Get all the labels displayed
        const metricsLabels = document.getElementsByClassName(classes.metricLabel);
        if (metricsLabels.length > 0) {
            // Use one of the labels and get the font settings for that label. To calculate the width of the metric correctly, we will
            // need to get the font settings
            const metricLabel = metricsLabels[0];
            const style = window.getComputedStyle(metricLabel);
            const fontStyle = `${style['font-weight']} ${style['font-size']} ${style['font-family']}`;

            // Get the longest metrics' name in pixel based on the label's font style and then add 45 for the width of the checkbox
            // next to the label and 10 for +- padding
            const longestMetricNameInPixel = getLongestMetricNameInPixel(
                props.numericMetrics, props.tagMetrics, undefined, undefined, fontStyle,
            ) + 55;

            // Make sure the width is at least 100 and less than 250 pixels
            setMetricLabelWidthInPixel(Math.max(100, Math.min(250, longestMetricNameInPixel)));
        }
    }, [classes.metricLabel, props.numericMetrics, props.tagMetrics]);



    return (
        <SlideOutComp
            compContent={(
                <Paper className={clsx(panelClasses.root, 'transparent', 'editing')}>
                    <div className={clsx(panelClasses.panelContent)}>
                        <form className={clsx(formClasses.root)} noValidate autoComplete="on" onSubmit={onFormSubmit}>
                            {(props.numericMetrics.length > 0 || props.tagMetrics.length > 0) ? (
                                <div className={formClasses.formInputGroup}>
                                    <FormControl
                                        required
                                        error={Boolean(inputErrors.selectedMetrics)}
                                        className={classes.formControl}
                                    >
                                        <div>
                                            <FormLabel
                                                className={classes.formControlLabel}
                                            >{capitalizeFirst(trans('panel.multi_data_sources.export.select_metrics.label'))}
                                            </FormLabel>

                                            {/* Only show 'Select All' options if there are more than 1 data to select */}
                                            {props.numericMetrics.length + props.tagMetrics.length > 1 && (
                                                <Button
                                                    className={classes.selectAllButton}
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => {
                                                        toggleSelectAll(!areAllSelected);
                                                    }}
                                                >
                                                    {capitalizeFirst(trans(areAllSelected ? 'label.unselect_all' : 'label.select_all'))}
                                                </Button>
                                            )}
                                        </div>

                                        <FormGroup className={clsx(classes.formGroup)}>
                                            {props.numericMetrics.map((metric) => {
                                                return (
                                                    <FormControlLabel
                                                        key={metric.id}
                                                        className={classes.metric}
                                                        control={(
                                                            <Checkbox
                                                                value={selectedMetricsMap[metric.id]}
                                                                checked={selectedMetricsMap[metric.id]}
                                                                color="primary"
                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                    onToggleDataSelection(metric.id, event.target.checked);
                                                                }}
                                                            />
                                                        )}
                                                        style={{
                                                            width: `${metricLabelWidthInPixel}px`,
                                                        }}
                                                        label={(
                                                            <div className={classes.metricLabel}>
                                                                {metric.displayName ?? metric.name ?? metric.id}
                                                            </div>
                                                        )}
                                                        labelPlacement="end"
                                                    />
                                                );
                                            })}

                                            {props.tagMetrics.map((metric) => {
                                                return (
                                                    <FormControlLabel
                                                        key={metric.id}
                                                        className={classes.metric}
                                                        control={(
                                                            <Checkbox
                                                                value={selectedMetricsMap[metric.id]}
                                                                checked={selectedMetricsMap[metric.id]}
                                                                color="primary"
                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                    onToggleDataSelection(metric.id, event.target.checked);
                                                                }}
                                                            />
                                                        )}
                                                        style={{
                                                            width: `${metricLabelWidthInPixel}px`,
                                                        }}
                                                        label={(
                                                            <div className={classes.metricLabel}>
                                                                {metric.displayName ?? metric.name ?? metric.id}
                                                            </div>
                                                        )}
                                                        labelPlacement="end"
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                        <FormHelperText>{inputErrors.selectedMetrics}</FormHelperText>
                                    </FormControl>

                                    {/*
                                        Only show the list of format options if there are more than 1 options.
                                    */}
                                    {props.supportedDataFormats.length > 1 && (
                                        <ModeSelectInputField
                                            fieldName="format"
                                            isInEditMode
                                            label={capitalizeFirst(trans('panel.multi_data_sources.export.select_format.label'))}
                                            editable
                                            required
                                            options={props.supportedDataFormats.map((option) => {
                                                return {
                                                    label: trans(option.displayNameKey),
                                                    value: option,
                                                } as SelectInputOption<ExportDataFormat>;
                                            })}
                                            className={formClasses.formInputField}
                                            value={supportedDataFormats}
                                            onInputChange={(inputName, newInputValue) => {
                                                onFormatChanged(newInputValue);
                                            }}
                                        />
                                    )}

                                    <ModeTextInputField
                                        fieldName="fileName"
                                        isInEditMode
                                        label={capitalizeFirst(trans('panel.multi_data_sources.export.enter_file_name.label'))}
                                        editable
                                        required
                                        className={formClasses.formInputField}
                                        value={selectedFileName}
                                        error={inputErrors.fileName}
                                        onInputChange={(inputName, newInputValue) => {
                                            onFileNameChanged(newInputValue);
                                        }}
                                    />
                                </div>
                            ) : (
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                    })}
                                />
                            )}
                            <div className={clsx(formClasses.formActionContainer)}>
                                <div />
                                <div>
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        onClick={() => {
                                            hidePreviewPanel();
                                        }}
                                    >
                                        {trans('label.cancel')}
                                    </Button>
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        disabled={Boolean(
                                            props.numericMetrics.length + props.tagMetrics.length === 0
                                            || inputErrors.selectedMetrics || inputErrors.fileName,
                                        )}
                                    >
                                        {trans('label.export')}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>
                </Paper>
            )}
            closable={false}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
